"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/lib/auth/client";
import type { SavedSearchFilters } from "@/lib/db/queries/saved-searches";
import {
	POST_APPLY_ALERT_SOURCE,
	parsePostApplyReturn,
	stripPostApplyReturnParams,
} from "@/lib/experiments/post-apply-single-ask";

type Outcome = "saved" | "limit" | "error";

/**
 * Return trip of the post-apply-single-ask card. The magic link lands the
 * now-signed-in guest back on the page they were on (job detail or the feed,
 * tagged `?alert_capture=post_apply&alert_q=…|alert_cat=…`). Mounted by the
 * layout-level `ConfirmApplyToast`, so it runs on every locale page.
 *
 * Honoured even if the flag was turned off since — the visitor already asked
 * for the alert (same rule as JobDetailAlertCapture). The params are read
 * from the browser URL only, never server-side, so cached HTML never varies.
 * A guest (link opened in another browser, session lost) gets nothing: there
 * is no account to attach the alert to.
 */
export function PostApplyAlertReturn({ locale }: { locale?: string }) {
	const posthog = usePostHog();
	const { data: session, isPending } = useSession();
	const [filters, setFilters] = useState<SavedSearchFilters | null>(null);
	const [outcome, setOutcome] = useState<Outcome | null>(null);
	const ran = useRef(false);
	const isId = locale !== "en";

	useEffect(() => {
		try {
			setFilters(parsePostApplyReturn(window.location.search));
		} catch {
			// No usable location — a normal visit.
		}
	}, []);

	useEffect(() => {
		if (!filters || isPending || ran.current) return;
		ran.current = true;
		try {
			window.history.replaceState(
				window.history.state,
				"",
				stripPostApplyReturnParams(window.location.href),
			);
		} catch {
			// Cosmetic only.
		}
		if (!session?.user) return;
		fetch("/api/saved-searches", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({
				filters,
				locale: isId ? "id" : "en",
				source: POST_APPLY_ALERT_SOURCE,
			}),
		})
			.then((res) => {
				const next: Outcome = res.ok
					? "saved"
					: res.status === 409
						? "limit"
						: "error";
				setOutcome(next);
				posthog?.capture("post_apply_alert_return", {
					outcome: next,
					has_query: Boolean(filters.q),
					locale: isId ? "id" : "en",
				});
			})
			.catch(() => setOutcome("error"));
	}, [filters, isPending, session, isId, posthog]);

	useEffect(() => {
		if (!outcome) return;
		const timer = setTimeout(() => setOutcome(null), 5000);
		return () => clearTimeout(timer);
	}, [outcome]);

	if (!outcome) return null;
	const text =
		outcome === "saved"
			? isId
				? "Beres — loker baru akan masuk ke email kamu tiap pagi."
				: "Done — new jobs will land in your inbox every morning."
			: outcome === "limit"
				? isId
					? "Kamu sudah mencapai batas alert. Hapus alert lama dulu."
					: "You've reached the alert limit. Remove an older alert first."
				: isId
					? "Gagal menyimpan alert. Coba lagi dari halaman lowongan."
					: "Couldn't save the alert. Try again from the jobs page.";
	return (
		// @sticky-slot-ok: 5-second status toast that stacks ABOVE the sticky
		// Apply/ad bars via --sticky-cta-h/--ad-bar-h (same placement as
		// ConfirmApplyToast), so it never takes the footer slot.
		<output
			data-testid="post-apply-alert-return"
			data-outcome={outcome}
			className="fixed inset-x-4 bottom-[calc(var(--sticky-cta-h,0px)+var(--ad-bar-h,0px)+1rem)] z-50 mx-auto block max-w-sm rounded-xl border border-border bg-card p-4 text-sm text-card-foreground shadow-lg sm:left-auto sm:right-4 lg:bottom-4"
		>
			{text}
		</output>
	);
}
